import { Injectable, Optional } from '@angular/core';

import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { EsportTrackingValues, NOT_APPLICABLE, TrackCrossSaleData, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { ModularConfigAccessorService } from '@frontend/sports/widget/core/feature';
import { isNil } from 'lodash-es';

import { BannerTrackingModel, GymlTrackingModel, VymlTrackingModel } from './banner.model';
import { CrossSaleTracking } from './cross-sale-tracking.model';

@Injectable({ providedIn: 'root' })
export class BannerTrackingService {
    constructor(
        private trackingService: TrackingService,
        @Optional() private configAccessor: ModularConfigAccessorService | null,
    ) {}

    clearBannerTracking(): void {
        this.trackingService.update({
            [trackingConstants.PAGE_DISPLAYED_ELEMENT]: '',
            [trackingConstants.PAGE_REFERRING_ACTION]: '',
        });
    }

    trackEsportBanner(isCloseEvent: boolean): void {
        this.trackingService.track(isCloseEvent ? trackingConstants.EVENT_TRACKING : trackingConstants.EVENT_CONTENT_VIEW, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Sports Teaser Banners',
            [trackingConstants.COMPONENT_LABEL_EVENT]: EsportTrackingValues.Esports,
            [trackingConstants.COMPONENT_ACTION_EVENT]: isCloseEvent ? 'close' : 'load',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.configAccessor?.getPage() ?? NOT_APPLICABLE,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: EsportTrackingValues.BannerMessage,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }

    trackGymlLaunch(trackingParams: GymlTrackingModel): void {
        this.trackingService.track(trackingConstants.EVENT_GAME_OPEN, {
            [trackingConstants.COMPONENT_GAME_SECTION]: CrossSaleTracking.GameSectionGyml,
            [trackingConstants.COMPONENT_GAME_POSITION]: trackingParams.index,
            [trackingConstants.COMPONENT_GAME_NAME]: trackingParams.name,
            [trackingConstants.COMPONENT_GAME_TYPE]: trackingParams.provider,
            [trackingConstants.COMPONENT_GAME_DISPLAY_NAME]: trackingParams.displayName,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: trackingParams.index?.toString() ?? NOT_APPLICABLE,
        });

        this.trackCrossSell({
            crossSellProduct: CrossSaleTracking.Casino,
            lobbyPosition: this.getLobbyPosition(trackingParams.lobbyPosition, trackingParams.pageName),
            gameName: trackingParams.name,
            url: trackingParams.url,
            crossSellName: CrossSaleTracking.Gyml,
            crossSellActionEvent: CrossSaleTracking.CrossSellClick,
            index: trackingParams.index,
        });
    }

    trackGymlSeeAllCrossSell(lobbyPosition: string, url: string): void {
        this.trackCrossSell({
            crossSellProduct: CrossSaleTracking.Casino,
            lobbyPosition: this.getLobbyPosition(lobbyPosition, ''),
            gameName: '',
            url,
            crossSellName: CrossSaleTracking.Gyml,
            crossSellActionEvent: CrossSaleTracking.SeeAllClick,
        });
    }

    trackLinkGameLaunch(name: string): void {
        this.trackingService.track(trackingConstants.EVENT_GAME_OPEN, {
            [trackingConstants.COMPONENT_GAME_SECTION]: CrossSaleTracking.GameSectionLinkBanner,
            [trackingConstants.COMPONENT_GAME_NAME]: name,
        });
    }

    trackJackpotGameLaunch(name: string): void {
        this.trackingService.track(trackingConstants.EVENT_GAME_OPEN, {
            [trackingConstants.COMPONENT_GAME_SECTION]: CrossSaleTracking.GameSectionJackpotBanner,
            [trackingConstants.COMPONENT_GAME_NAME]: name,
        });
    }

    trackBannerClickCrossSell(name: string, lobbyPosition: string, url: string): void {
        this.trackCrossSell({
            crossSellProduct: CrossSaleTracking.Casino,
            lobbyPosition: this.getLobbyPosition(lobbyPosition, ''),
            gameName: name,
            url,
            crossSellName: CrossSaleTracking.JackpotBanner,
            crossSellActionEvent: CrossSaleTracking.CrossSellClick,
        });
    }

    trackVymlLaunch(trackingParams: VymlTrackingModel): void {
        const tracking: Record<string, string | number> = {
            [trackingConstants.COMPONENT_GAME_NAME]: trackingParams.name,
            [trackingConstants.COMPONENT_GAME_POSITION]: trackingParams.index,
            [trackingConstants.COMPONENT_GAME_SECTION]: trackingParams.section,
            [trackingConstants.COMPONENT_GAME_DISPLAY_NAME]: trackingParams.displayName,
            [trackingConstants.COMPONENT_GAME_TYPE]: CrossSaleTracking.Fralis,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: trackingParams.index?.toString() ?? NOT_APPLICABLE,
        };

        if (trackingParams.section === CrossSaleTracking.GameSectionOnDemand) {
            tracking[trackingConstants.COMPONENT_GAME_GRID_POSITION] = NOT_APPLICABLE;
            tracking[trackingConstants.COMPONENT_GAME_STATUS] = NOT_APPLICABLE;
            tracking[trackingConstants.COMPONENT_GAME_CONTAINER_LOCATION] = NOT_APPLICABLE;
            tracking[trackingConstants.COMPONENT_GAME_CONTAINER_DESCRIPTION] = NOT_APPLICABLE;
            tracking[trackingConstants.COMPONENT_GAME_FAVOURITE] = NOT_APPLICABLE;
            tracking[trackingConstants.COMPONENT_GAME_PLAYING_STATUS] = NOT_APPLICABLE;
            tracking[trackingConstants.COMPONENT_CATEGORY_TYPE] = CrossSaleTracking.Virtuals;
            tracking[trackingConstants.COMPONENT_CATEGORY_SUB_TYPE] = CrossSaleTracking.VirtualGames;
        }

        this.trackingService.track(trackingConstants.EVENT_VIRTUAL_OPEN, tracking);

        if (trackingParams.section === CrossSaleTracking.GameSectionVyml) {
            this.trackCrossSell({
                crossSellProduct: CrossSaleTracking.Virtuals,
                lobbyPosition: this.getLobbyPosition(trackingParams.lobbyPosition, trackingParams.pageName),
                gameName: trackingParams.displayName,
                url: trackingParams.url,
                crossSellName: CrossSaleTracking.Vyml,
                crossSellActionEvent: CrossSaleTracking.CrossSellClick,
                index: trackingParams.index,
            });
        }
    }

    private trackCrossSell(trackingModel: BannerTrackingModel): void {
        const crossSaleData: TrackCrossSaleData & {
            [trackingConstants.COMPONENT_CONTENT_POSITION]: string | undefined;
        } = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: CrossSaleTracking.CrossSellCategoryEvent,
            [trackingConstants.COMPONENT_LABEL_EVENT]: trackingModel.crossSellProduct,
            [trackingConstants.COMPONENT_ACTION_EVENT]: trackingModel.crossSellActionEvent,
            [trackingConstants.COMPONENT_POSITION_EVENT]: trackingModel.lobbyPosition,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: trackingModel.crossSellName,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: trackingModel.gameName,
            [trackingConstants.COMPONENT_URL_CLICKED]: trackingModel.url,
        } as TrackCrossSaleData & { [trackingConstants.COMPONENT_CONTENT_POSITION]: string | undefined };

        if (!isNil(trackingModel.index)) {
            crossSaleData[trackingConstants.COMPONENT_CONTENT_POSITION] = trackingModel.index.toString();
        }

        this.trackingService.track(trackingConstants.EVENT_CLICKS, crossSaleData);
    }

    private getLobbyPosition(position: string, pageName: string): string {
        if (pageName === RouteTag.LiveSport) {
            return CrossSaleTracking.MidPage;
        }

        if (pageName === RouteTag.SportList || pageName === RouteTag.Horses) {
            return CrossSaleTracking.BottomPage;
        }

        switch (position) {
            case 'LobbyTopBanner':
                return CrossSaleTracking.TopPage;
            case 'LobbyMiddleBanner':
                return CrossSaleTracking.MidPage;
            case 'LobbyBottomBanner':
                return CrossSaleTracking.BottomPage;
            case NOT_APPLICABLE:
                return NOT_APPLICABLE;
            default:
                return '';
        }
    }
}
